import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Pagination,
  Box,
  Button,
  TextField,
} from '@mui/material';
import api from '../globals/axios';
import CustomAppBar from '../components/AppBar';
import LoaderContainer from '../components/LoaderContainer';
import { useTranslation } from 'react-i18next';
import { CustomTitle } from '../components/Custom/CustomTypography';
import CustomCard from '../components/Famer/GenericCard';
// import ConfirmationPopup from "../components/Popup/GenericPopup";
import { useAlert } from '../components/Alert';
import { isNotNullOrEmptyArray, acresToHectares } from '../globals/utils';
import GenericButton from '../components/Custom/GenericButton';
import { saveAuth, saveToLocal, getAuth } from '../globals/authentication';
import CustomFilter from '../components/Custom/CustomFilter';

export default function FarmersList({ role }) {
  const [farmers, setFarmers] = useState([]);
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // const [selectedFarmerId, setSelectedFarmerId] = useState(null);
  // const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const initialSearchText = searchParams.get('search') || '';
  const [searchText, setSearchText] = useState(initialSearchText);
  const [pagination, setPagination] = useState({
    page: page,
    total_pages: 0,
    total_objects: 0,
  });

  useEffect(() => {
    fetchFarmers(pagination.page);
  }, []);

  const fetchFarmers = async (page = 1) => {
    try {
      let url = `/usermanagement/get-sub-users?farmers=true&page=${page}`;
      if (searchText) {
        url += `&search=${searchText}`;
      }
      const response = await api.get(url);
      if (response && response.data) {
        const { results, current_page_number, total_pages, total_objects } =
          response.data.data.farmers;
        setFarmers(results);
        setPagination({
          page: current_page_number,
          total_pages: total_pages,
          total_objects: total_objects,
        });

        setSearchParams({
          page: current_page_number.toString(),
          ...(searchText && { search: searchText }),
        });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
      showAlert('error', 'Error fetching Farmers');
    }
  };

  // Uncomment the following function if you want to enable delete functionality
  // const handleDelete = (id) => {
  //   setSelectedFarmerId(id);
  //   setDeleteConfirmationOpen(true);
  // };

  // Uncomment the following function if you want to enable delete functionality
  // const handleConfirmDelete = async () => {
  //   try {
  //     await api.delete(`/auth/delete-account/?user_id=${selectedFarmerId}`);
  //     showAlert("success", "Farmer Deleted Successfully");
  //     fetchFarmers();
  //   } catch (err) {
  //     const errorMessage =
  //       err.response?.data?.message || "Error deleting the Farmer.";
  //     showAlert("error", errorMessage);
  //   }
  //   setDeleteConfirmationOpen(false);
  // };

  const handlePageChange = (_, value) => {
    setPage(value);
    setPagination({ ...pagination, page: value });
    fetchFarmers(value);
  };

  const handleFilterFarmers = () => {
    setPage(1);
    setPagination({ ...pagination, page: 1 });
    fetchFarmers();
  };

  const handleTransferFarmer = id => {
    navigate(`/vle-transfer/${id}`);
  };

  const handleAddFarmer = () => {
    navigate('/farmer/add');
  };

  const handleUpdatefarmer = id => {
    navigate(`/farmer/edit/${id}`);
  };

  const handleLoginFarmer = async id => {
    try {
      const currentAuthData = getAuth();
      const response = await api.post(`/vle/farmer-login/?persona_user=${id}`);
      const authData = JSON.stringify(response.data);

      if (currentAuthData && currentAuthData.is_internal_login) {
        window.location.replace(`/login-internal/?authDetails=${authData}`);
      } else {
        if (currentAuthData) {
          saveToLocal('auth_backup', {
            ...currentAuthData,
            redirectionURL: window.location.href,
          });
        }
        saveAuth(authData);
        window.location.replace(`/login-internal/?authDetails=${authData}`);
      }
    } catch (err) {
      console.error(err);
      showAlert('error', 'Error logging in as Farmer');
    }
  };

  const handleJumpToPage = () => {
    if (!isNaN(page) && page >= 1 && page <= pagination.total_pages) {
      setPagination({ ...pagination, page: page });
      fetchFarmers(page);
    } else {
      showAlert('error', 'Enter a valid page number');
    }
  };

  return (
    <>
      <Helmet>
        <title>My Farmers - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container
          maxWidth="xs"
          style={{
            height: farmers.length === 0 ? '58vh' : '94vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <GenericButton
              onClick={handleAddFarmer}
              backgroundColor="primary"
              fontColor={'#fff'}
              fontSize={'14px'}
              padding={'7px 10px'}
            >
              {t('Add Farmer')}
            </GenericButton>
          </div>
          <CustomFilter
            searchText={searchText}
            setSearchText={setSearchText}
            handleFilterClick={handleFilterFarmers}
          />
          {!isNotNullOrEmptyArray(farmers) ? (
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: 'center',
                fontSize: 24,
                fontWeight: 600,
                margin: 'auto',
              }}
            >
              {t(
                'No farmers found. Your Farmers will be shown here once you add them.'
              )}
            </Typography>
          ) : (
            <>
              <CustomTitle>{t('My Farmers')}</CustomTitle>
              <div
                style={{
                  overflowY: 'auto',
                  maxHeight: 'calc(115vh - 300px)',
                  textAlign: 'center',
                }}
              >
                {farmers.map(farmer => (
                  <CustomCard
                    key={farmer.id}
                    id={farmer.id}
                    t={t}
                    data={{
                      [t('Name')]: farmer.full_name,
                      [t('Username')]: farmer.username,
                      [t('Phone')]: farmer.phone_number,
                      [t('Village')]: farmer?.village || 'N/A',
                      [t('Area')]: `${farmer.area} ${t(
                        'acres'
                      )} (${acresToHectares(farmer.area)} ${t('hectares')})`,
                      ...(role === 'AREA_MANAGER' ||
                        (role === 'POP_MANAGER' && {
                          [t('VLE')]: farmer.vle.full_name,
                        })),
                      ...(role === 'POP_MANAGER' && {
                        [t('Area Manager')]: farmer.area_manager.full_name,
                      }),
                      [t('Crops')]: isNotNullOrEmptyArray(farmer?.commodities)
                        ? farmer.commodities.join(', ')
                        : 'N/A',
                    }}
                    handlePrimaryButton={() => handleLoginFarmer(farmer.id)}
                    {...((role === 'AREA_MANAGER' ||
                      role === 'POP_MANAGER') && {
                      handleSecondaryButton: () =>
                        handleTransferFarmer(farmer.id),
                      secondaryButtonLabel: t('Transfer'),
                    })}
                    handleEditClick={() => handleUpdatefarmer(farmer.id)}
                    primaryLabel={t('Login')}
                    hideEdit={false}
                  />
                ))}
              </div>
            </>
          )}
          {pagination?.total_pages > 1 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '20px 0',
                }}
              >
                <Pagination
                  count={pagination.total_pages}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  maxWidth: '400px',
                  margin: '0 auto',
                }}
              >
                <Typography>{t('page')}:</Typography>
                <TextField
                  type="number"
                  value={page}
                  onChange={e => setPage(e.target.value)}
                  size="small"
                  sx={{
                    width: '60px',
                    input: {
                      padding: '6px 10px',
                      textAlign: 'center',
                    },
                  }}
                />

                <Typography variant="body2">
                  of {pagination.total_pages}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleJumpToPage}
                  sx={{ textTransform: 'none' }}
                >
                  {t('Go')}
                </Button>
              </Box>
            </>
          )}
        </Container>
      </LoaderContainer>
      {/* <ConfirmationPopup
        open={deleteConfirmationOpen}
        handleClose={() => setDeleteConfirmationOpen(false)}
        handleConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this Farmer?"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      /> */}
    </>
  );
}
